import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ShieldCheckIcon } from "@/components/icons";
import { useAuth } from "@/src/auth-context";
import { useDocumentMeta } from "@/src/use-document-meta";

export function ApplyPage() {
  const { id = "" } = useParams();
  const { user, loading, openLogin } = useAuth();
  const [attempt, setAttempt] = useState<ApplyAttempt>({ state: "idle" });

  useDocumentMeta({
    title: "Apply on Chuno",
    description: "A private, signed-in application experience on Chuno.",
    robots: "noindex,nofollow",
    canonical: `https://www.chuno.work/apply/${encodeURIComponent(id)}`,
  });

  useEffect(() => {
    if (!loading && !user) openLogin(`/apply/${encodeURIComponent(id)}`);
  }, [id, loading, openLogin, user]);

  useEffect(() => {
    if (!user || !id || attempt.state !== "idle") return;
    const storageKey = `chuno:apply-click:${id}`;
    const eventId = sessionStorage.getItem(storageKey) || crypto.randomUUID();
    sessionStorage.setItem(storageKey, eventId);

    void recordApplyClick(user, id, eventId).then((result) => {
      if (result.allowed) {
        if (sessionStorage.getItem(storageKey) === eventId) sessionStorage.removeItem(storageKey);
        setAttempt({ state: "allowed", count: result.count, remaining: result.remaining });
      } else {
        if (result.limited && sessionStorage.getItem(storageKey) === eventId) sessionStorage.removeItem(storageKey);
        setAttempt({ state: result.limited ? "limited" : "error" });
      }
    });
  }, [attempt.state, id, user]);

  if (!id) return <Navigate to="/jobs" replace />;
  if (loading) return <main id="main" className="page-loading"><div><span />Checking your account…</div></main>;

  if (!user) {
    return (
      <main id="main" className="apply-workspace">
        <div className="shell apply-workspace__card">
          <p className="eyebrow"><span /> Sign in required</p>
          <h1>Keep your application private.</h1>
          <p>Sign in to continue. This application will stay within Chuno.</p>
          <button className="button button--dark" type="button" onClick={() => openLogin(`/apply/${encodeURIComponent(id)}`)}>Sign in to apply</button>
          <Link className="back-link" to={`/jobs/${encodeURIComponent(id)}`} reloadDocument><ArrowLeft /> Back to the role</Link>
        </div>
      </main>
    );
  }

  if (attempt.state === "idle") {
    return <main id="main" className="page-loading"><div><span />Checking your apply limit…</div></main>;
  }

  if (attempt.state === "limited") {
    return (
      <main id="main" className="apply-workspace">
        <div className="shell apply-workspace__card">
          <p className="eyebrow"><span /> Apply limit reached</p>
          <h1>You’ve used your 50 apply clicks.</h1>
          <p>Your account cannot start another application. You can still review and save roles on Chuno.</p>
          <Link className="button button--dark" to="/jobs">Browse jobs <ArrowUpRight /></Link>
        </div>
      </main>
    );
  }

  if (attempt.state === "error") {
    return (
      <main id="main" className="apply-workspace">
        <div className="shell apply-workspace__card">
          <p className="eyebrow"><span /> Could not continue</p>
          <h1>Please try again.</h1>
          <p>We couldn’t verify this apply click. Your allowance was not intentionally consumed.</p>
          <button className="button button--dark" type="button" onClick={() => setAttempt({ state: "idle" })}>Try again</button>
        </div>
      </main>
    );
  }

  return (
    <main id="main" className="apply-workspace">
      <div className="shell apply-workspace__card">
        <p className="eyebrow"><span /> Chuno application</p>
        <h1>You’re signed in.</h1>
        <p>Your secure Chuno application space is ready for <strong>{user.email || "your account"}</strong>.</p>
        <div className="apply-workspace__assurance"><ShieldCheckIcon /><span>Protected by your Chuno account</span></div>
        <p className="apply-workspace__remaining">{attempt.remaining} of 50 apply clicks remaining</p>
        <Link className="button button--dark" to={`/jobs/${encodeURIComponent(id)}`} reloadDocument>Review the role <ArrowUpRight /></Link>
      </div>
    </main>
  );
}

type ApplyAttempt =
  | { state: "idle" }
  | { state: "limited" }
  | { state: "error" }
  | { state: "allowed"; count: number; remaining: number };

async function recordApplyClick(user: NonNullable<ReturnType<typeof useAuth>["user"]>, jobId: string, eventId: string) {
  try {
    const token = await user.getIdToken();
    const response = await fetch(`/api/apply-clicks/${encodeURIComponent(jobId)}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ event_id: eventId }),
    });
    const body = await response.json() as { allowed?: boolean; count?: number; remaining?: number };
    if (response.ok && body.allowed) {
      return { allowed: true as const, limited: false, count: body.count || 0, remaining: body.remaining ?? 0 };
    }
    return { allowed: false as const, limited: response.status === 429, count: 0, remaining: 0 };
  } catch {
    return { allowed: false as const, limited: false, count: 0, remaining: 0 };
  }
}
